import { z } from "zod";
import { MCPConfigSchema } from "./mcp-schemas";

// MCP is a first-class connector entity, fully decoupled from System.
// The backend (api-ai-transform /v1/mcp) returns these rows; MCP used to
// be modeled as a System with kind === "mcp" + System.mcp, but that coupling is
// gone. The field renames vs the old System-based MCP are:
//   - system_id          → mcp_id
//   - top-level `mcp`     → top-level `config` (an MCPConfig)
//   - there is no `kind`  on MCP
//
// WIRE NOTE: the MCP RUNTIME endpoints (/v1/mcp/connect, /v1/mcp/discover,
// /v1/mcp/tools|prompts|resources, /v1/mcp/catalog/*, resources/subscribe + SSE)
// now take the JSON field `mcp_id` (renamed from `system_id` to match this
// entity id). Two surfaces still use `system_id`: the /v1/mcp/oauth-callback
// response + the paired MCP_OAUTH_COMPLETE postMessage, and the separate
// /v1/auth-config plane — both carry this `mcp_id` value under their own
// `system_id` field.
// Read-path schema — intentionally loose. The list endpoint returns whatever is
// in the DB, including half-created/never-discovered rows; strict requires
// (`.min`, `.datetime`) would make one bad row reject the entire list and blank
// the page. Keep field presence/types stable (so components don't need null
// guards) but drop value constraints. Strict validation lives in
// MCPPayloadSchema (create/update forms), not here.
export const MCPSchema = z.object({
  mcp_id: z.string(),
  name: z.string().optional(),
  description: z.string().optional(),
  ai_summary: z.string().optional(),
  // status mirrors the backend string enum: "active" | "inactive" | "archived".
  status: z.string().optional(),
  created_time: z.string().optional(),
  last_updated_time: z.string().optional(),
  // Config carries the connection + discovery cache + tool policy (the block
  // that used to live under System.mcp).
  config: MCPConfigSchema.optional(),
});

export type MCP = z.infer<typeof MCPSchema>;

// Create/update payload. config is required on create; on update the backend
// performs a selective overwrite (only present fields change, Config is
// replaced wholesale when supplied).
export const MCPPayloadSchema = z.object({
  name: z
    .string()
    .min(1, "MCP server name is required")
    .max(255, "MCP server name must be 255 characters or less"),
  description: z
    .string()
    .max(1000, "Description must be 1000 characters or less")
    .optional(),
  config: MCPConfigSchema,
});

export type MCPPayload = z.infer<typeof MCPPayloadSchema>;

// Update payload: every field is optional (selective overwrite server-side).
export const MCPUpdatePayloadSchema = MCPPayloadSchema.partial();
export type MCPUpdatePayload = z.infer<typeof MCPUpdatePayloadSchema>;
